import { Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Button } from '@repo/foundations';
import { invalidateNotificationListQuery } from '../../../../api/hooks/notification/utils';
import type { EnhancedNotification } from '../../types';

interface TeamAccessRequestActionProps {
	notification: EnhancedNotification;
	approveTeamAccessRequest: (params: {
		teamId: string;
		membershipId: string;
	}) => Promise<unknown>;
	rejectTeamAccessRequest: (params: {
		teamId: string;
		membershipId: string;
	}) => Promise<unknown>;
	isApprovingTeamAccessRequest: boolean;
	isRejectingTeamAccessRequest: boolean;
}

export function TeamAccessRequestAction({
	notification,
	approveTeamAccessRequest,
	rejectTeamAccessRequest,
	isApprovingTeamAccessRequest,
	isRejectingTeamAccessRequest,
}: TeamAccessRequestActionProps) {
	const teamId = notification.metadata?.team_id as string | undefined;
	const membershipId = notification.metadata?.membership_id as
		| string
		| undefined;
	const status = notification.metadata?.status as string | undefined;

	if (!teamId || !membershipId || status !== 'pending') {
		return null;
	}

	const onApprove = async () => {
		try {
			await approveTeamAccessRequest({ teamId, membershipId });
			invalidateNotificationListQuery();
			showNotification({
				title: 'Access request approved',
				message: 'The user can now access the team',
				color: 'green',
			});
		} catch {
			showNotification({
				title: 'Could not approve request',
				message: 'Please refresh and try again',
				color: 'red',
			});
		}
	};

	const onReject = async () => {
		try {
			await rejectTeamAccessRequest({ teamId, membershipId });
			invalidateNotificationListQuery();
			showNotification({
				title: 'Access request rejected',
				message: 'The request has been declined',
				color: 'green',
			});
		} catch {
			showNotification({
				title: 'Could not reject request',
				message: 'Please refresh and try again',
				color: 'red',
			});
		}
	};

	return (
		<Group spacing="xs">
			<Button
				size="md"
				variant="default"
				onClick={onReject}
				loading={isRejectingTeamAccessRequest}
			>
				Reject
			</Button>
			<Button
				size="md"
				variant="primary"
				onClick={onApprove}
				loading={isApprovingTeamAccessRequest}
			>
				Approve
			</Button>
		</Group>
	);
}
